import { forwardRef, ReactElement, Ref, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { ETopLeagueLayoutType } from 'interfaces/sportsbook-configs';
import { Region } from 'interfaces/sportsbook-data-levels';
import { useCompetitionClick } from './hooks/useCompetitionClick';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { CompetitionNameWithTooltip } from 'components/Shared/CompetitionNameWithTooltip';
import { CompetitionsFavorite } from 'newcomponents/Shared/sportsbook/CompetitionsFavorite';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setCompetitionMetaData } from 'store/actions';
import { setNavigationWrapperHeightChanged } from 'store/actions/new-sportsbook';
import { getRegionsSortingOption } from 'store/selectors/new-sportsbook';
import {
  getClickFromFavorites,
  getScrollToCompetitionId
} from 'store/selectors/sport-data';
import './index.less';

type TRegionLevelNavigationProps = {
  navItemsToRender: Region[];
  sportAlias: string;
  itemsSliceCount?: number;
};

export const RegionLevelNavigation = forwardRef(
  (
    {
      navItemsToRender,
      sportAlias,
      itemsSliceCount
    }: TRegionLevelNavigationProps,
    scrollToRef: Ref<HTMLDivElement>
  ): ReactElement => {
    const dispatch = useDispatch();
    const { params } = useSbRouteMatch();
    const competitionClickHandler = useCompetitionClick();
    const configs = useSportsbookConfigs();
    const regionsSortingOption = useSelector(getRegionsSortingOption);

    useScrollToTop('prematch-left-sidebar-wrapper', [params?.sport]);

    const scrollToCompetitionId = useSelector(getScrollToCompetitionId);
    const clickFromFavorites = useSelector(getClickFromFavorites);
    const routeCompetitionInData = navItemsToRender.find(
      region => region.alias === params.region
    )?.competition?.[params.competition];

    useEffect(() => {
      if (params.category === ENotRealSports.team) {
        return;
      }

      const shouldNotSelectFirstCompetition =
        !params.sport &&
        SpringConfigs.TOP_LEAGUES &&
        SpringConfigs.TOP_LEAGUES_LAYOUT === ETopLeagueLayoutType.compact &&
        SpringConfigs.SPORT_MENU_DEFAULT_SELECTED === 'topLeagues';

      if (shouldNotSelectFirstCompetition || clickFromFavorites) {
        return;
      }

      if (
        navItemsToRender.length &&
        !isMobile() &&
        ((!params.category && !params.region && !params.competition) ||
          !routeCompetitionInData)
      ) {
        const firstCompetition = sortByOrder(
          Object.values(navItemsToRender[0]?.competition || {})
        )[0];

        if (firstCompetition) {
          competitionClickHandler({
            sportAlias: sportAlias,
            regionAlias: navItemsToRender[0].alias,
            compId: firstCompetition.id
          });
        }
      }
    }, [params, navItemsToRender, clickFromFavorites]);

    useEffect(() => {
      if (routeCompetitionInData && routeCompetitionInData.name) {
        dispatch(setCompetitionMetaData({ name: routeCompetitionInData.name }));
      }
    }, [routeCompetitionInData?.name]);

    const getDefaultActive = (index: number, region: Region) => {
      const isCurrentRegionInRoute =
        !!params.region && params.region === region.alias;

      return (
        ((!Number(SpringConfigs.SPORT_COMPETITION_IS_COMPACT) || !isMobile()) &&
          index === 0 &&
          !params.region) ||
        (!!scrollToCompetitionId &&
          !!region.competition?.[scrollToCompetitionId?.id]) ||
        isCurrentRegionInRoute
      );
    };

    const onToggle = (active: boolean) => {
      if (!active && !isMobile() && !configs.fixedScroll) {
        dispatch(setNavigationWrapperHeightChanged());
      }
    };

    const orderedData = useMemo(() => {
      if (regionsSortingOption.key && navItemsToRender) {
        return sortByOrder(
          navItemsToRender,
          regionsSortingOption.key,
          regionsSortingOption.type
        );
      }

      return navItemsToRender;
    }, [navItemsToRender, regionsSortingOption.key, regionsSortingOption.type]);

    const ViewCollapse = useMemo(
      () => (isMobile() ? EventCollapse : NavigationCollapse),
      []
    );

    return (
      <div
        className={cc([
          'navigationCollapseWrapper',
          { 'navigationCollapseWrapper--mobile': isMobile() }
        ])}
      >
        {orderedData
          .slice(0, itemsSliceCount || navItemsToRender.length)
          .map((region, index) => (
            <ViewCollapse
              renderInDom={SpringConfigs.RENDER_COMPETITIONS_IN_DOM}
              wrapperBackColor="var(--v3-black-1)"
              title={region.name}
              key={region.id}
              icon={
                <GlobalIcon
                  size={EIconSize._24}
                  name={region.alias}
                  lib="flags"
                />
              }
              defaultActive={getDefaultActive(index, region)}
              dataTestId="region"
              onChange={onToggle}
            >
              <>
                {sortByOrder(Object.values(region.competition || {})).map(
                  comp => (
                    <CompetitionNameWithTooltip
                      key={comp.id}
                      dataTestId="competition"
                      active={params.competition === String(comp.id)}
                      competitionItem={comp}
                      clickHandler={e =>
                        competitionClickHandler({
                          sportAlias: sportAlias,
                          regionAlias: region.alias,
                          compId: comp.id,
                          e
                        })
                      }
                      competitionUrl={competitionClickHandler({
                        sportAlias: sportAlias,
                        regionAlias: region.alias,
                        compId: comp.id,
                        returnURLOnly: true
                      })}
                      IconBeforeText={
                        <CompetitionsFavorite
                          iconSize={EIconSize._16}
                          competitionInfo={{
                            id: comp.id,
                            name: comp.name,
                            regionAlias: region.alias,
                            sportAlias
                          }}
                        />
                      }
                      {...(isMobile() &&
                        scrollToCompetitionId?.id === comp.id.toString() && {
                          ref: scrollToRef
                        })}
                    />
                  )
                )}
              </>
            </ViewCollapse>
          ))}
      </div>
    );
  }
);
